import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { MassOverwriteButton } from '@/common/entityFramework/entityPerspective/overwrite/MassOverwriteButton';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef } from 'react';
import { TheraSelect } from '../../../theraSelect/TheraSelect';

export const AbvRbtPipeOverUnderPerspective = ({ overUnderScenId }) => {
  const entityPerspectiveRef = useRef();
  const theraSelectRef = useRef();

  const entity = 'RBT_PIPE_OVER_UNDER';

  const views = [
    {
      name: 'Full Breakout',
      dims: ['OVER_UNDER_SCEN', 'CHANNEL', 'PROD'],
      kpis: [
        'QTR_RBT_PIPE_RQMT',
        'MON_RBT_PIPE_RQMT',
        'PIPE_BAL',
        'LBE_PIPE_ADJ',
        'PL_REASONABLENESS_PROPOSAL',
        'TOTAL_PL_ADJ',
        'BS_RECLASS',
        'BS_REASONABLENESS_PROPOSAL',
        'TOTAL_BS_ADJ',
        'TOTAL_ADJ',
        'PROPOSED_BAL',
        'MIN_MONTH',
        'MIDPOINT',
        'MAX_MONTH',
        'START_MOS',
        'END_MOS',
        'VALUE_OF_ONE_TICK',
        'OVER_UNDER_LOW_AMT',
        'OVER_UNDER_AMT',
        'OVER_UNDER_HIGH_AMT',
      ],
      disabledDims: ['OVER_UNDER_SCEN'],
      numFrozenDims: 1,
    },
  ];

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    let proposedAdjSubMenu = [
      {
        name: 'Balance Pipeline Reclass',
        action: () =>
          entityPerspectiveRef.current.navigateToPerspectiveAdj(
            params,
            'BS_REASONABLENESS_PROPOSAL',
            'B/S Reasonableness Proposal',
          ),
        icon: fas.faSlidersH,
      },
      {
        name: 'P/L Pipeline Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToPerspectiveAdj(
            params,
            'PL_REASONABLENESS_PROPOSAL',
            'P/L Reasonableness Proposal',
          ),
        icon: fas.faSlidersH,
      },
    ];
    contextMenus.push(
      {
        name: 'Adjustments',
        subMenu: proposedAdjSubMenu,
        icon: fas.faSlidersH,
      },
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params),
        icon: fas.faArrowCircleDown,
      },
    );

    return contextMenus;
  };

  const defaultDimFilters = { OVER_UNDER_SCEN: [overUnderScenId] };

  const handleViewReset = useCallback(() => {
    onViewReset();
    theraSelectRef.current.reset();
  }, []);

  const events = {
    onDimFilterChange: ({ dimCode }) => {
      if (dimCode === 'PROD') {
        theraSelectRef.current.reset();
      }
    },
  };

  const augKpis = (kpi) => {
    if (kpi.code === 'OVER_UNDER_LOW_AMT') {
      kpi.cellStyle = (p) => (p.value < 0 ? { backgroundColor: '#fde2e2' } : undefined);
    }

    if (kpi.code === 'OVER_UNDER_HIGH_AMT') {
      kpi.cellStyle = (p) => (p.value > 0 ? { backgroundColor: '#fde2e2' } : undefined);
    }
  };

  const augButtons = (buttons) => {
    buttons.unshift(
      <TheraSelect entityPerspectiveRef={entityPerspectiveRef} ref={theraSelectRef} />,
      <MassOverwriteButton
        toolTip={'Adjust Rebate Pipeline Over/(Under)'}
        kpiCodes={['PL_REASONABLENESS_PROPOSAL', 'BS_REASONABLENESS_PROPOSAL', 'BS_REASONABLENESS_PROPOSAL']}
        entity={entity}
        defaultDimValues={{ OVER_UNDER_SCEN: overUnderScenId }}
      />,
      ...filterButtons,
      <TitleButtonDivider key="filterButtonDivider" />,
    );
  };

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'isInRange',
      icon: fas.faExclamationTriangle,
      colors: [null, 'red', 'green'],
      toolTips: ['', 'Not in range', 'In Range'],
    },
  ]);

  const augReqParam = useCallback(
    (reqParam) => {
      // In Range
      const inRangeButtonState = filterButtonStages.isInRange;
      if (inRangeButtonState > 0) {
        reqParam.expr = ExprUtils.and(
          reqParam.expr,
          inRangeButtonState === 1 ? ExprUtils.whereParams('isInRange', 0) : ExprUtils.whereParams('isInRange', 1),
        );
      }
    },
    [filterButtonStages],
  );

  const rowIconsFn = (data) => {
    let rowIcons = [];

    // In range row icons
    if (data.isInRange === 0) {
      rowIcons.push(<RowIcon key="isNotInRange" icon={fas.faExclamationTriangle} color="red" toolTip="Not in range" />);
    } else if (data.isInRange !== 1) {
      rowIcons.push(
        <RowIcon
          key="someNotInRange"
          icon={fas.faExclamationTriangle}
          color="orange"
          toolTip="Contains not in range"
        />,
      );
    }

    return rowIcons;
  };

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      entity={entity}
      augKpis={augKpis}
      rowIconsFn={rowIconsFn}
      augReqParam={augReqParam}
      defaultDimFilters={defaultDimFilters}
      onViewReset={handleViewReset}
      augButtons={augButtons}
      events={events}
      views={views}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
